import type NativeArray from '@ember/array/-private/native-array';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import type DeviceModel from 'garaje/models/device';
import type PropertyPrinterModel from 'garaje/models/property-printer';
import type ZoneModel from 'garaje/models/zone';
import type DeviceStatusService from 'garaje/services/device-status-service';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type { RecordArray } from 'garaje/utils/type-utils';
import { hash } from 'rsvp';

export interface PropertyDevicesRouteModel {
  ipads: RecordArray<DeviceModel> | DeviceModel[];
  printers: RecordArray<PropertyPrinterModel> | NativeArray<PropertyPrinterModel>;
  property: ZoneModel;
}

export default class PropertyDevicesRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service declare deviceStatusService: DeviceStatusService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare router: RouterService;
  @service declare store: Store;

  beforeModel(): void {
    if (this.abilities.cannot('manage devices for property')) {
      void this.router.transitionTo('unauthorized');
    }
  }

  async model(): Promise<PropertyDevicesRouteModel> {
    const property = <ZoneModel>this.modelFor('property');

    if (this.featureFlags.isEnabled('property-devices-service-status-api')) {
      // Load printers first: the sideloaded `devices` relationship hydrates the
      // printer association onto the (stub) device records, then loadIpads pushes
      // the devices-service telemetry on top — which preserves that relationship
      // since the telemetry push doesn't touch `printer`. (Loading concurrently
      // would let the printer sideload clobber the telemetry, or vice versa.)
      const printers = await this.loadPrinters(property.id);
      const ipads = await this.loadIpads(property.id);

      return hash({ ipads, printers, property });
    }

    return hash({
      ipads: this.loadIpads(property.id),
      printers: this.loadPrinters(property.id),
      property,
    });
  }

  private async loadPrinters(
    propertyId: string,
  ): Promise<RecordArray<PropertyPrinterModel> | NativeArray<PropertyPrinterModel>> {
    return this.store.query('property-printer', {
      filter: {
        property: propertyId,
      },
      include: 'devices,property',
    });
  }

  private async loadIpads(zoneId: string): Promise<RecordArray<DeviceModel> | DeviceModel[]> {
    // Property iPads have their own company-trafficked flag (the visitors flag is
    // location-trafficked, and a split is bound to a single traffic type).
    if (this.featureFlags.isEnabled('property-devices-service-status-api')) {
      // Property iPads use the 'visitors' product like location iPads, but the
      // devices-service scopes them by zone_id (their location_id is null). Fetch
      // by zone and build records from telemetry the same way the visitors iPads
      // route does.
      await this.deviceStatusService.ensureStatuses(zoneId, 'visitors', 'zone');
      const statuses = Array.from(this.deviceStatusService.statusByDeviceId.values());
      return statuses
        .filter((s) => s.product === 'visitors' && String(s.zoneId) === String(zoneId))
        .map((s) => {
          const normalized = this.store.normalize('device', {
            type: 'devices',
            id: s.externalId,
            attributes: {
              ...s.telemetry,
              'is-sleeping': s.telemetry?.['sleeping'] ?? false,
              'is-connected': s.isConnected,
              'last-heartbeat-at': s.connectionStatusUpdatedAt?.toISOString() ?? null,
            },
            relationships: {
              zone: { data: { type: 'zones', id: zoneId } },
            },
          });
          return this.store.push(normalized) as DeviceModel;
        })
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    return this.store.query('device', {
      filter: {
        zone: zoneId,
      },
      include: 'printer',
    });
  }
}
